/**
 * Error Page
 *
 * Template displayed when any client side route fails.
 * Tries to pull the Error Page modules, if they exist.
 * Otherwise, shows a default 404 message.
 */

// Deps
import get from "lodash.get";
import errorPageData from "~/data/error-page-data.json";
import { getColorSet } from "~/modules/colors";
import { createComponent } from "react-fela";
import { gutters } from "~/styleguide";
import { textStyles } from "~/styleguide/typography";
import { fluid } from "~/styleguide/utils";
import { useRouter } from "next/router";

// Contexts
import { ThemeContext } from "~/context/theme";
import { DataContext } from "~/context/content";
import ContentContext from "~/context/content";
import PageTransition from "~/components/motion/page-transition";

// Components
import Page from "~/components/templates/page";

export default function ErrorPage() {
    const { asPath } = useRouter();

    // Fire a 404 event to Google Analytics
    if (asPath && typeof window !== "undefined" && window.ga) {
        window.ga("send", "event", "404", process.env.ENVIRONMENT, asPath);
    }

    const modules = get(errorPageData, "acf.modules");

    // If no Error page is registered, show a default 404 page
    if (!errorPageData || !modules || !modules.length)
        return (
            <BacukUpError>
                <h1>Sorry, this page could not be found</h1>
            </BacukUpError>
        );

    const pageColors = getColorSet(
        get(errorPageData, "acf.theme_primary_color", "sky")
    );

    // Otherwise, load the blocks of the Error page
    return (
        <PageTransition>
            <ThemeContext.Provider value={pageColors}>
                <DataContext.Provider value={errorPageData}>
                    <ContentContext
                        layout="detail"
                        type="page"
                        data={errorPageData}
                    >
                        <Page page={errorPageData} />
                    </ContentContext>
                </DataContext.Provider>
            </ThemeContext.Provider>
        </PageTransition>
    );
}

// TODO: create default 404 page contianer
const BacukUpError = createComponent(() => ({
    textAlign: "center",
    "> h1": textStyles.heading2,
    extend: [
        fluid("paddingV", { max: 300, min: 100 }),
        fluid("paddingH", gutters.default)
    ]
}));
